import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, RotateCcw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const money = (n) => `$${(Number(n) || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const INSUMO_LABELS = { caseina: "Caseína", base: "Base", cuajo: "Cuajo", sal: "Sal", cloruro: "Cloruro de calcio" };
const labelOf = (t) => ({ mp: "Leche / MP directo", cream: "Crema", curd: "Cuajada", insumo: "Insumo" }[t] || t);
const conceptOf = (it) => (it.source_type === "insumo" ? (INSUMO_LABELS[it.source_folio] || it.source_folio) : labelOf(it.source_type));

// --- Conversión de número a letras (pesos mexicanos) ---
const UNIDADES = ["", "UN", "DOS", "TRES", "CUATRO", "CINCO", "SEIS", "SIETE", "OCHO", "NUEVE"];
const ESPECIALES_10_19 = ["DIEZ", "ONCE", "DOCE", "TRECE", "CATORCE", "QUINCE", "DIECISÉIS", "DIECISIETE", "DIECIOCHO", "DIECINUEVE"];
const DECENAS = ["", "", "VEINTE", "TREINTA", "CUARENTA", "CINCUENTA", "SESENTA", "SETENTA", "OCHENTA", "NOVENTA"];
const CENTENAS = ["", "CIENTO", "DOSCIENTOS", "TRESCIENTOS", "CUATROCIENTOS", "QUINIENTOS", "SEISCIENTOS", "SETECIENTOS", "OCHOCIENTOS", "NOVECIENTOS"];
const VEINTI_ACENTOS = { 1: "ÚN", 2: "DÓS", 3: "TRÉS", 6: "SÉIS" };

function convertirGrupo(num) {
  if (num === 0) return "";
  if (num === 100) return "CIEN";
  const c = Math.floor(num / 100);
  const resto = num % 100;
  let letras = c > 0 ? CENTENAS[c] : "";
  let deci = "";
  if (resto > 0) {
    if (resto < 10) deci = UNIDADES[resto];
    else if (resto < 20) deci = ESPECIALES_10_19[resto - 10];
    else {
      const d = Math.floor(resto / 10);
      const u = resto % 10;
      if (d === 2) deci = u === 0 ? "VEINTE" : `VEINTI${VEINTI_ACENTOS[u] || UNIDADES[u]}`;
      else deci = u === 0 ? DECENAS[d] : `${DECENAS[d]} Y ${UNIDADES[u]}`;
    }
  }
  return [letras, deci].filter(Boolean).join(" ");
}

function numeroALetras(n) {
  n = Math.floor(n);
  if (n === 0) return "CERO";
  const millones = Math.floor(n / 1000000);
  const restoMM = n % 1000000;
  const miles = Math.floor(restoMM / 1000);
  const resto = restoMM % 1000;
  let partes = [];
  if (millones > 0) partes.push(millones === 1 ? "UN MILLÓN" : `${convertirGrupo(millones)} MILLONES`);
  if (miles > 0) partes.push(miles === 1 ? "MIL" : `${convertirGrupo(miles)} MIL`);
  if (resto > 0) partes.push(convertirGrupo(resto));
  // "de pesos" cuando el número son SOLO millones exactos (sin miles ni resto), ej. "un millón de pesos"
  if (millones > 0 && miles === 0 && resto === 0) partes.push("DE");
  return partes.join(" ").trim();
}

function montoALetras(amount) {
  let entero = Math.floor(amount);
  let centavos = Math.round((amount - entero) * 100);
  if (centavos >= 100) { entero += 1; centavos = 0; } // evita "100/100" por redondeo
  const sustantivo = entero === 1 ? "PESO" : "PESOS";
  return `${numeroALetras(entero)} ${sustantivo} ${String(centavos).padStart(2, "0")}/100 M.N.`;
}

export default function EntregaProducto() {
  const { saleId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchDoc = async (regenerate = false) => {
    setBusy(true);
    try {
      const { data: d } = await api.post(`/sales/${saleId}/delivery-document?regenerate=${regenerate}`, {});
      setData(d);
      if (regenerate) toast.success("Documento regenerado con los datos actuales del cliente");
    } catch (err) {
      const msg = err.response?.data?.detail || "No se pudo generar el documento";
      if (regenerate) toast.error(msg); // conserva el documento ya cargado, solo avisa
      else setError(msg); // primera carga fallida: sí reemplaza la página
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { fetchDoc(false); }, [saleId]);

  const regenerate = () => {
    if (window.confirm("Esto reemplazará los datos del cliente/pagaré guardados en este documento con la información ACTUAL del cliente. ¿Continuar?")) {
      fetchDoc(true);
    }
  };

  if (error) return <div className="p-10 text-center text-red-600" data-testid="entrega-error">{error}</div>;
  if (!data) return <div className="p-10 text-center text-stone-500" data-testid="entrega-loading">Generando documento…</div>;

  const { sale, document: doc, pagare_amount, missing_fields } = data;
  const isCredito = sale.payment_method === "credito";
  const emitido = new Date(doc.regenerated_at || doc.created_at);

  return (
    <div className="min-h-screen bg-stone-100 dark:bg-stone-950" data-testid="entrega-page">
      {/* Toolbar (no imprime) */}
      <div className="print:hidden bg-white dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800 p-4 flex items-center justify-between flex-wrap gap-3">
        <Button variant="outline" onClick={() => window.close() || window.history.back()}><ArrowLeft className="w-4 h-4 mr-1" /> Cerrar</Button>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={regenerate} disabled={busy} data-testid="regenerate-btn"><RotateCcw className="w-4 h-4 mr-1" /> Regenerar con datos actuales</Button>
          <Button onClick={() => window.print()} data-testid="print-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]"><Printer className="w-4 h-4 mr-1" /> Imprimir / PDF</Button>
        </div>
      </div>

      {missing_fields.length > 0 && (
        <div className="print:hidden max-w-3xl mx-auto mt-4 p-3 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-800 flex items-start gap-2" data-testid="missing-fields-banner">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800 dark:text-amber-300">
            <b>Faltan datos del cliente para completar este documento:</b> {missing_fields.join(", ")}.
            Complétalos en <a href="/clientes" target="_blank" rel="noopener noreferrer" className="underline font-medium">Clientes</a> y usa "Regenerar" aquí, o continúa — los campos faltantes se mostrarán en blanco ("—") en el documento.
          </div>
        </div>
      )}

      {/* Documento imprimible */}
      <div className="max-w-3xl mx-auto p-6 lg:p-10 print:p-0" data-testid="entrega-content">
        <div className="bg-white print:bg-white shadow-sm print:shadow-none border border-stone-200 print:border-0 p-8 print:p-6 text-stone-900">

          {/* Header */}
          <div className="flex items-start justify-between pb-4 border-b-2 border-[#1E3A8A]">
            <div>
              <div className="text-xl font-outfit font-bold text-[#1E3A8A]">LOGÍSTICA LAGUENSE S.A. DE C.V.</div>
              <div className="text-xs text-stone-500 mt-1">RFC: LLA130816RFA</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.15em] font-outfit font-bold text-stone-700">ENTREGA DE PRODUCTO Y PAGARÉ</div>
              <div className="text-lg font-outfit font-bold text-stone-900" data-testid="doc-folio">{sale.folio}</div>
              <div className="text-xs text-stone-500">Emitido: {emitido.toLocaleDateString("es-MX")} {emitido.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}</div>
            </div>
          </div>

          {/* Cliente */}
          <div className="grid grid-cols-2 gap-6 mt-5">
            <div>
              <div className="text-[9px] uppercase tracking-[0.15em] font-outfit font-semibold text-stone-500 mb-1.5">Cliente</div>
              <div className="text-base font-outfit font-bold" data-testid="doc-customer-name">{doc.customer_name}</div>
              <div className="text-xs text-stone-600 mt-1">RFC: {doc.customer_rfc || "—"}</div>
              <div className="text-xs text-stone-600">Tel: {doc.customer_phone || "—"}</div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-[0.15em] font-outfit font-semibold text-stone-500 mb-1.5">Domicilio de entrega</div>
              <div className="text-sm text-stone-800" data-testid="doc-delivery-address">{doc.delivery_address || "—"}</div>
              <div className="text-xs text-stone-500 mt-2">Fecha de venta: {sale.sale_date}</div>
            </div>
          </div>

          {/* Productos */}
          <div className="mt-6">
            <div className="text-[9px] uppercase tracking-[0.15em] font-outfit font-semibold text-stone-500 mb-1.5">Productos entregados</div>
            <div className="overflow-x-auto print:overflow-visible">
            <table className="w-full border border-stone-300 min-w-[560px] print:min-w-0">
              <thead className="bg-stone-100">
                <tr>
                  {["Cantidad", "Unidad", "Concepto / Producto / Lote", "P. Unitario", "Importe"].map((h) => (
                    <th key={h} className="px-2 py-1.5 text-left text-[9px] uppercase tracking-wider font-outfit font-bold text-stone-600 border-b border-stone-300">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(sale.items || []).map((it, i) => (
                  <tr key={i} className="border-b border-stone-200" data-testid={`doc-item-${i}`}>
                    <td className="px-2 py-1.5 text-sm tabular-nums">{it.quantity}</td>
                    <td className="px-2 py-1.5 text-sm">{it.unit}</td>
                    <td className="px-2 py-1.5 text-sm">{conceptOf(it)} <span className="text-stone-500 font-mono text-xs">· Lote {it.source_folio}</span></td>
                    <td className="px-2 py-1.5 text-sm tabular-nums">{money(it.unit_price)}</td>
                    <td className="px-2 py-1.5 text-sm tabular-nums font-medium">{money(it.subtotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            {sale.notes && (
              <div className="mt-2 text-xs text-stone-600"><b>Observaciones:</b> {sale.notes}</div>
            )}

            <div className="flex justify-end mt-3">
              <div className="w-56 text-sm">
                <div className="flex justify-between py-0.5"><span className="text-stone-600">Subtotal</span><span className="tabular-nums">{money(sale.total)}</span></div>
                <div className="flex justify-between py-0.5"><span className="text-stone-600">IVA (exento)</span><span className="tabular-nums">$0.00</span></div>
                <div className="flex justify-between py-1 border-t border-stone-300 mt-1 font-outfit font-bold"><span>Total</span><span className="tabular-nums" data-testid="doc-total">{money(sale.total)}</span></div>
              </div>
            </div>
          </div>

          {/* Condición de pago */}
          <div className="mt-4 flex items-center gap-2">
            <span className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-500">Condición:</span>
            {isCredito ? (
              <span className="inline-block px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-medium">Crédito {sale.credit_days}d · Vence {sale.due_date || "—"} · Saldo {money(pagare_amount)}</span>
            ) : (
              <span className="inline-block px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 text-xs font-medium">Pagado de contado</span>
            )}
          </div>

          {/* Condiciones de entrega */}
          <div className="mt-6 p-3 bg-stone-50 border border-stone-200 rounded text-[11px] text-stone-700 leading-relaxed">
            El cliente revisa y recibe el producto descrito, verificando cantidad, presentación y condiciones al momento de la entrega. Una vez que el producto salga de nuestras instalaciones, no se acepta ningún tipo de cambio o devolución, salvo los casos que legalmente correspondan. A partir de la entrega, el cliente se obliga a mantener el transporte, manejo y cadena de frío adecuados.
          </div>

          {/* Firmas de entrega */}
          <div className="mt-10 grid grid-cols-2 gap-8">
            <div>
              <div className="border-t border-stone-400 pt-2 text-center text-xs text-stone-600">Nombre y firma de quien entrega</div>
            </div>
            <div>
              <div className="border-t border-stone-400 pt-2 text-center text-xs text-stone-600">Nombre y firma de quien recibe y acepta</div>
              <div className="text-center text-[10px] text-stone-500 mt-1">{doc.customer_name}</div>
            </div>
          </div>

          {/* PAGARÉ — solo ventas a crédito */}
          {isCredito && (
            <div className="mt-10 pt-6 border-t-2 border-dashed border-stone-400" style={{ breakBefore: "page" }} data-testid="pagare-section">
              <div className="text-center font-outfit font-bold text-lg uppercase tracking-wider mb-4">Pagaré</div>
              <div className="flex justify-between text-sm mb-3">
                <span><b>No. de pagaré:</b> {doc.pagare_number || "—"}</span>
                <span><b>Importe:</b> {money(pagare_amount)}</span>
              </div>
              <div className="text-sm leading-relaxed text-justify">
                En <b>{doc.pagare_place || "________________"}</b>, a {sale.sale_date}, yo <b>{doc.signer_name || "________________"}</b>
                {doc.signer_capacity ? <> (<b>{doc.signer_capacity}</b>)</> : null}, con domicilio en <b>{doc.signer_address || "________________"}</b>,
                debo y pagaré incondicionalmente a la orden de <b>LOGÍSTICA LAGUENSE S.A. DE C.V.</b> en esta plaza o en <b>{doc.payment_place || "________________"}</b>,
                la cantidad de <b>{money(pagare_amount)}</b> ({montoALetras(pagare_amount)}), valor recibido a mi entera satisfacción por concepto del producto descrito en este documento (venta {sale.folio}).
              </div>
              <div className="text-sm leading-relaxed text-justify mt-3">
                Este pagaré es pagadero el día <b>{sale.due_date || "________________"}</b>, en <b>{doc.payment_place || "________________"}</b>.
              </div>
              <div className="mt-10 grid grid-cols-1 gap-2">
                <div className="w-72 mx-auto">
                  <div className="border-t border-stone-400 pt-2 text-center text-xs text-stone-600">Firma del suscriptor / representante</div>
                  <div className="text-center text-[10px] text-stone-500 mt-1">{doc.signer_name || doc.customer_name}{doc.signer_capacity ? ` · ${doc.signer_capacity}` : ""}</div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 pt-4 border-t border-stone-200 text-[9px] text-center text-stone-400">
            Documento generado por sistema ERP LácteoPro el {new Date(doc.created_at).toLocaleString("es-MX")}
            {doc.regenerated_at && ` · Regenerado el ${new Date(doc.regenerated_at).toLocaleString("es-MX")}`}
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: letter; margin: 1.2cm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}
